import { Injectable } from '@angular/core'
import { DbServiceData, DbServiceMetaData } from '@shared/decorators'
import { from, Observable } from 'rxjs'

/**
 * The IndexedDB service provides generic methods to interact with IndexedDB.
 */
@Injectable()
export class IndexedDBService<T = DbServiceData> implements DbServiceMetaData {
  /** @override set the db name @default `${userID}_DB` if InjectableDb decorator is used else '_DB'*/
  dbName = '_DB' as const

  /** @override set the store name */
  storeName = ''

  /** @override set the index key name @default id */
  indexKeyName = 'id' as const

  /** @override set the version @default 1 */
  version = 1

  /** @override set the auto increment @default false */
  autoIncrement? = false

  /**
   * Need to set to true to open DB connection
   * Will be set to true once all the meta data is initialized
   * @see InjectableDb
   */
  private metaDataInitialized = false

  private dbReady: Promise<IDBDatabase>

  constructor() {
    this.dbReady = this.openDB()
  }

  /**
   * Save data into the object store
   */
  public upsertData(data: T): Observable<T> {
    return from(
      this.ready().then((db) => {
        if (!(data as Record<string, unknown>)[this.indexKeyName]) {
          throw new Error(`Data object must include a property '${this.indexKeyName}'`)
        }

        const transaction = db.transaction(this.storeName, 'readwrite')
        const store = transaction.objectStore(this.storeName)
        const request = store.put(data)

        return this.handleRequest<T>(request, 'Saving')
      }),
    )
  }

  /**
   * Get all data from the object store
   */
  public retrieveAll(): Observable<T[]> {
    return from(
      this.ready().then((db) => {
        const transaction = db.transaction(this.storeName, 'readonly')
        const store = transaction.objectStore(this.storeName)
        const request = store.getAll()

        return this.handleRequest<T[]>(request, 'Retrieving all data')
      }),
    )
  }

  /**
   * Get data by ID from the object store
   */
  public retrieveItem(key: number | string): Observable<T | undefined> {
    return from(
      this.ready().then((db) => {
        const transaction = db.transaction(this.storeName, 'readonly')
        const store = transaction.objectStore(this.storeName)
        const request = store.get(key)

        return this.handleRequest<T | undefined>(request, 'Retrieving data by ID')
      }),
    )
  }

  /**
   * Delete data by ID from the object store
   */
  public removeItem(key: number | string): Observable<void> {
    return from(
      this.ready().then((db) => {
        const transaction = db.transaction(this.storeName, 'readwrite')
        const store = transaction.objectStore(this.storeName)
        const request = store.delete(key)

        return this.handleRequest<void>(request, 'Deleting data')
      }),
    )
  }

  /**
   * Open the IndexedDB database and create an object store if needed
   */
  private openDB(): Promise<IDBDatabase> {
    return this.waitDataInitialization().then(
      () =>
        new Promise((resolve, reject) => {
          const request = indexedDB.open(this.dbName, this.version)

          request.onupgradeneeded = (event) => {
            const db = (event.target as IDBRequest).result
            if (!db.objectStoreNames.contains(this.storeName)) {
              const options: IDBObjectStoreParameters = {
                keyPath: this.indexKeyName,
              }
              if (this.autoIncrement) {
                options.autoIncrement = true
              }
              db.createObjectStore(this.storeName, options)
            }
          }

          request.onsuccess = (event) => {
            const db = (event.target as IDBRequest).result
            resolve(db)
          }

          request.onerror = (event) => {
            console.error('Error opening IndexedDB:', event)
            reject(new Error('Error opening IndexedDB'))
          }
        }),
    )
  }

  private waitDataInitialization() {
    return new Promise((resolve) => {
      if (this.metaDataInitialized) {
        resolve(true)
      } else {
        const interval = setInterval(() => {
          if (this.metaDataInitialized) {
            clearInterval(interval)
            resolve(true)
          }
        }, 100)
      }
    })
  }

  /**
   * Wait for the database to be ready
   */
  private ready(): Promise<IDBDatabase> {
    return this.dbReady
  }

  /**
   * Helper method to handle the result of an IDBRequest
   */
  private handleRequest<U>(request: IDBRequest, action: string): Promise<U> {
    return new Promise((resolve, reject) => {
      request.onsuccess = () => resolve(request.result as U)
      request.onerror = (event) => {
        console.error(`Error:: ${action}:`, event)
        reject({ message: `Error:: ${action}`, event })
      }
    })
  }
}
